/* C23 · qt-cart-progress.js — the one threshold bar (cart drawer, cart bar, gift pop-up).
   Numbers come from data attributes (Liquid) and, after every cart change, from /cart.js (cents).
   The gift segment exists only inside the gift window, evaluated here at view time (C16).
   Text templates come from locale keys; [amount] / [gift] are inserted as text nodes (no HTML
   interpolation). Spec: 01-componenten.md C23. */
import { register, onCartUpdate, parseDate, subtotalWithoutGift, fetchCart } from '@qt/core';
import { formatMoney } from '@theme/money-formatting';

const SELECTOR = '[data-qt-progress]';

function giftIsLive(el) {
  const threshold = Number(el.dataset.giftThreshold);
  if (!threshold) return false;
  const start = parseDate(el.dataset.giftStart, 'qt_gift_start');
  const end = parseDate(el.dataset.giftEnd, 'qt_gift_end');
  if (start === null || end === null) return false;
  const now = Date.now();
  return now >= start && now <= end;
}

/** Which threshold the sentence names, and how far the bar is towards it. */
function state(subtotal, shipping, gift) {
  if (shipping > 0 && subtotal < shipping) {
    return { text: 'to_shipping', remaining: shipping - subtotal, percent: (subtotal / shipping) * 100, reached: [], target: 'shipping' };
  }
  if (gift > 0 && subtotal < gift) {
    const floor = Math.max(shipping, 0);
    return { text: 'to_gift', remaining: gift - subtotal, percent: ((subtotal - floor) / (gift - floor)) * 100, reached: ['shipping'], target: 'gift' };
  }
  return {
    text: gift > 0 ? 'all_reached' : 'shipping_reached',
    remaining: 0,
    percent: 100,
    reached: gift > 0 ? ['shipping', 'gift'] : ['shipping'],
    target: null,
  };
}

function fill(el, key, values) {
  const template = el.querySelector(`template[data-text="${key}"]`);
  if (!template) return document.createDocumentFragment();
  const fragment = template.content.cloneNode(true);
  const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    node.nodeValue = node.nodeValue.replace(/\[(amount|gift)\]/g, (_, token) => values[token] ?? '');
  }
  return fragment;
}

function render(el) {
  const subtotal = Number(el.dataset.subtotal) || 0;
  const shipping = Number(el.dataset.shipping) || 0;
  const live = giftIsLive(el);
  const gift = live ? Number(el.dataset.giftThreshold) : 0;
  const s = state(subtotal, shipping, gift);
  const money = (cents) => formatMoney(cents, el.dataset.moneyFormat, el.dataset.currency);
  const done = s.target === null;
  // Round down so 2999 of 3000 cents shows an almost-full bar, never a full one.
  const percent = done ? 100 : Math.min(99, Math.floor(Math.max(0, s.percent)));

  const line = el.querySelector('.qt-progress__line');
  line.replaceChildren(fill(el, s.text, { amount: money(s.remaining), gift: el.dataset.giftTitle }));
  line.classList.toggle('is-reached', done);

  const bar = el.querySelector('.qt-progress__bar');
  bar.classList.toggle('is-reached', done);
  bar.setAttribute('aria-valuenow', String(percent));
  bar.querySelector('i').style.setProperty('--qt-progress', `${percent}%`);

  for (const point of el.querySelectorAll('[data-point]')) {
    const name = point.dataset.point;
    if (name === 'gift') point.hidden = !live;
    point.classList.toggle('is-reached', s.reached.includes(name));
    point.classList.toggle('is-target', s.target === name);
  }
}

function renderAll(subtotal) {
  for (const el of document.querySelectorAll(SELECTOR)) {
    if (subtotal !== undefined) el.dataset.subtotal = String(subtotal);
    render(el);
  }
}

let latestSubtotal;

async function refresh() {
  try {
    const cart = await fetchCart();
    latestSubtotal = subtotalWithoutGift(cart);
    renderAll(latestSubtotal);
    // C29: PDP shipping note follows the same subtotal
    for (const note of document.querySelectorAll('[data-qt-shipping-note]')) {
      const left = Number(note.dataset.threshold) - latestSubtotal;
      if (left > 0 && note.dataset.showRemaining !== 'true') continue;
      const key = left > 0 ? 'remaining' : 'reached';
      const money = formatMoney(left, note.dataset.moneyFormat, cart.currency);
      note.querySelector('[data-note-text]').replaceChildren(fill(note, key, { amount: money }));
    }
    // C23/C32: the bottom cart bar only exists while the cart has items
    document.querySelectorAll('[data-qt-cart-bar]').forEach((bar) => (bar.hidden = cart.item_count === 0));
  } catch {
    /* network error: keep the last rendered state */
  }
}

register(SELECTOR, { init: render });

// Horizon swaps the drawer contents when the cart goes from empty to filled; render new bars too.
new MutationObserver((mutations) => {
  for (const { addedNodes } of mutations) {
    for (const node of addedNodes) {
      if (!(node instanceof Element)) continue;
      const found = node.matches(SELECTOR) ? [node] : node.querySelectorAll(SELECTOR);
      for (const el of found) {
        if (latestSubtotal !== undefined) el.dataset.subtotal = String(latestSubtotal);
        render(el);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });

onCartUpdate(refresh);
